import {
  DailyOpeningHours,
  SpecialOpeningHours,
  TableId,
  Venue,
  WeeklyOpeningHours,
} from "@greeter/core";
import {
  DatePeriod,
  Day,
  LenientDatePeriod,
  TimeOfDay,
  withTimeOfDay,
} from "@greeter/date";
import { addDays, addSeconds, isSameDay } from "date-fns";
import { Res, Result } from "@greeter/result";

function findDailyOpeningHoursForTimeOfDay(
  openingHours: DailyOpeningHours[],
  arrivalTime: TimeOfDay,
) {
  for (let i = 0; i < openingHours.length; i++) {
    const openingHour = openingHours.at(i);

    if (!openingHour) continue;

    if (
      arrivalTime.getTotalSeconds() >= openingHour.from.getTotalSeconds() &&
      arrivalTime.getTotalSeconds() <= openingHour.to.getTotalSeconds()
    ) {
      return openingHour;
    }
  }
}

function isNextDay(openingHours: DailyOpeningHours, timeOfDay: TimeOfDay) {
  // .to passes midnight
  if (openingHours.to.getTotalSeconds() < openingHours.from.getTotalSeconds()) {
    return (
      timeOfDay.getTotalSeconds() < openingHours.to.getTotalSeconds() &&
      timeOfDay.getTotalSeconds() < openingHours.from.getTotalSeconds()
    );
  }

  return false;
}

export class NoOpeningHoursForDayError extends Error {}
export class NoOpeningHoursForArrivalTime extends Error {}

export type GetBookingPeriodErrors =
  | { code: "noOpeningHoursForDay"; data: Day }
  | { code: "noOpeningHoursForArrivalTime"; data: TimeOfDay };

export type GetBookingPeriodResult = Result<DatePeriod, GetBookingPeriodErrors>;

export type GetBookingPeriod2Errors = {
  code: "noOpeningHoursForDateTime";
  data: Date;
};

export type GetBookingPeriod2Result = Result<
  LenientDatePeriod,
  GetBookingPeriod2Errors
>;

export function getBookingPeriod2(
  /**
   * The dateperiods after dateperiods have been templated/normalized by opening hours, area opening hours, special opening hours etc.
   */
  periods: Array<LenientDatePeriod>,

  /**
   * The aggregated arrival time and arrival date.
   */
  arrivalDateAndTime: Date,
): GetBookingPeriod2Result {
  const period = periods.find((p) => p.isWithin(arrivalDateAndTime));

  if (!period) {
    return Res.error({
      code: "noOpeningHoursForDateTime",
      data: arrivalDateAndTime,
    });
  }

  return Res.ok(
    new LenientDatePeriod({
      from: arrivalDateAndTime,
      to: period.to,
    }),
  );
}

/**
 * @deprecated in favor of getBookingPeriod2, the reason being that this type of
 * funciton requires too much context in order not to be confusing.
 * What we want to do instead of convert all opening hours stuff to an array of
 * DatePeriods and filter throught that instead which vastly simplifies the function.
 */
export function getBookingPeriod(
  bookableOpeningHours: Venue["bookableOpeningHours"],
  specialOpeningHours: SpecialOpeningHours[],
  arrivalDate: Date,
  arrivalTime: TimeOfDay,
): GetBookingPeriodResult {
  const specialOpeningHour = specialOpeningHours.find((soh) =>
    isSameDay(soh.period.from, arrivalDate),
  );

  if (specialOpeningHour) {
    const spansMidnight = !isSameDay(
      specialOpeningHour?.period.from,
      specialOpeningHour?.period.to,
    );

    // TODO: What about past midnight
    return Res.ok(
      new DatePeriod({
        from: withTimeOfDay(
          spansMidnight ? addDays(arrivalDate, 1) : arrivalDate,
          arrivalTime,
        ),
        to: specialOpeningHour.period.to,
      }),
    );
  }

  const openingHoursForDay = bookableOpeningHours.get(arrivalDate.getDay());
  const normalized = WeeklyOpeningHours.normalize(bookableOpeningHours);

  if (!openingHoursForDay) {
    return Res.error({
      code: "noOpeningHoursForDay",
      data: arrivalDate.getDay() as Day,
    });
  }

  const _isNextDay = isNextDay(openingHoursForDay, arrivalTime);

  arrivalDate = addDays(arrivalDate, _isNextDay ? 1 : 0);

  const openingHours = normalized.get(arrivalDate.getDay());

  if (!openingHours) {
    return Res.error({
      code: "noOpeningHoursForDay",
      data: arrivalDate.getDay() as Day,
    });
  }

  let openingHour = findDailyOpeningHoursForTimeOfDay(
    openingHours,
    arrivalTime,
  );

  if (!openingHour) {
    return Res.error({
      code: "noOpeningHoursForArrivalTime",
      data: arrivalTime,
    });
  }

  if (!_isNextDay) {
    // This ensures that the booking spans the past midnight in cose of openinghours like
    // 23:00 - 04:00
    openingHour = openingHoursForDay;
  }

  const to = openingHour.to;

  const diff = arrivalTime.difference(to);
  return Res.ok(
    new DatePeriod({
      from: withTimeOfDay(arrivalDate, arrivalTime),
      to: addSeconds(
        withTimeOfDay(arrivalDate, arrivalTime),
        diff.getTotalSeconds(),
      ),
    }),
  );
}
